/**
 * Jemma V3, Sistema para perfilamiento de personas naturales y jurídicas
 * Copyright (c) 2025 JEMMA SOLUTIONS S.A.S.
 * Todos los derechos reservados.
 *
 * Servicios de listas de control (OFAC, antecedentes penales, homónimos,
 * providencias y PEPS) para persona natural.
 *
 * @author cfernandez
 * @version 1.0.0
 * @since 2026-04-08
 */
import {SearchParamsInterface} from "../interface/SearchParamsInterface";
import {useMutation, UseMutationResult} from "@tanstack/react-query";
import configAxios from "../configuration/configAxios";
import {interceptorResponse} from "../../utils/decode/decoding";
import {errorHandlerServices, getError} from "../configuration/errorHandle";
import {isValidIdentifier} from "../../utils/textOptions";

/**
 * Consulta coincidencias OFAC asociadas a la persona.
 *
 * @param data identificador de la persona
 * @returns listado de coincidencias OFAC descifrado
 */
export const findChecklistOfac = async (data: SearchParamsInterface) => {
    if (!isValidIdentifier(data.identifier)) return null;
    return await configAxios.post(
        `/control_lists/ofac`,
        {
            identifier: data.identifier
        }
    ).then((response) => {
        if (response.data.status !== 1) {
            throw new Error(response.data.message);
        } else {
            return interceptorResponse(response.data.object)
        }
    }).catch(error => {
        errorHandlerServices(error, true, '/control_lists/ofac', undefined, data?.msj)
        getError(error,
            error?.response?.data?.message ?
                error?.response?.data?.message : `Error service: /control_lists/ofac`);
    })
}

export const useFindChecklistOfac = (): UseMutationResult<any, Error, any, unknown> => {
    return useMutation({
        mutationFn: (data: SearchParamsInterface) => findChecklistOfac(data),
    });
}

/**
 * Verifica si la persona registra antecedentes penales.
 *
 * @param data identificador de la persona
 * @returns booleano indicando existencia de antecedentes
 */
export const findChecklistCriminalRecord = async (data: SearchParamsInterface) => {
    if (!isValidIdentifier(data.identifier)) return null;
    return await configAxios.post(
        `/control_lists/criminal_record`,
        {
            identifier: data.identifier
        }
    ).then((response) => {
        if (response.data.status !== 1) {
            throw new Error(response.data.message);
        } else {
            return interceptorResponse(response.data.object)
        }
    }).catch(error => {
        errorHandlerServices(error, true, '/control_lists/criminal_record', undefined, data?.msj)
        getError(error,
            error?.response?.data?.message ?
                error?.response?.data?.message : `Error service: /control_lists/criminal_record`);
    })
}

export const useFindChecklistCriminalRecord = (): UseMutationResult<any, Error, any, unknown> => {
    return useMutation({
        mutationFn: (data: SearchParamsInterface) => findChecklistCriminalRecord(data),
    });
}

/**
 * Consulta homónimos asociados a la persona.
 *
 * @param data identificador de la persona
 * @returns listado de homónimos descifrado
 */
export const findChecklistHomonyms = async (data: SearchParamsInterface) => {
    if (!isValidIdentifier(data.identifier)) return null;
    return await configAxios.post(
        `/control_lists/homonyms`,
        {
            identifier: data.identifier
        }
    ).then((response) => {
        if (response.data.status !== 1) {
            throw new Error(response.data.message);
        } else {
            return interceptorResponse(response.data.object)
        }
    }).catch(error => {
        errorHandlerServices(error, true, '/control_lists/homonyms', undefined, data?.msj)
        getError(error,
            error?.response?.data?.message ?
                error?.response?.data?.message : `Error service: /control_lists/homonyms`);
    })
}

export const useFindChecklistHomonyms = (): UseMutationResult<any, Error, any, unknown> => {
    return useMutation({
        mutationFn: (data: SearchParamsInterface) => findChecklistHomonyms(data),
    });
}

/**
 * Consulta providencias judiciales asociadas a la persona.
 *
 * @param data identificador de la persona
 * @returns listado de providencias descifrado
 */
export const findChecklistProvidences = async (data: SearchParamsInterface) => {
    if (!isValidIdentifier(data.identifier)) return null;
    return await configAxios.post(
        `/control_lists/providences`,
        {
            identifier: data.identifier
        }
    ).then((response) => {
        if (response.data.status !== 1) {
            throw new Error(response.data.message);
        } else {
            return interceptorResponse(response.data.object)
        }
    }).catch(error => {
        errorHandlerServices(error, true, '/control_lists/providences', undefined, data?.msj)
        getError(error,
            error?.response?.data?.message ?
                error?.response?.data?.message : `Error service: /control_lists/providences`);
    })
}

export const useFindChecklistProvidences = (): UseMutationResult<any, Error, any, unknown> => {
    return useMutation({
        mutationFn: (data: SearchParamsInterface) => findChecklistProvidences(data),
    });
}

/**
 * Consulta registros PEPS (Personas Expuestas Políticamente).
 *
 * @param data identificador de la persona
 * @returns listado de registros PEPS descifrado
 */
export const findChecklistPeps = async (data: SearchParamsInterface) => {
    if (!isValidIdentifier(data.identifier)) return null;
    return await configAxios.post(
        `/control_lists/peps`,
        {
            identifier: data.identifier
        }
    ).then((response) => {
        if (response.data.status !== 1) {
            throw new Error(response.data.message);
        } else {
            return interceptorResponse(response.data.object)
        }
    }).catch(error => {
        errorHandlerServices(error, true, '/control_lists/peps', undefined, data?.msj)
        getError(error,
            error?.response?.data?.message ?
                error?.response?.data?.message : `Error service: /control_lists/peps`);
    })
}

export const useFindChecklistPeps = (): UseMutationResult<any, Error, any, unknown> => {
    return useMutation({
        mutationFn: (data: SearchParamsInterface) => findChecklistPeps(data),
    });
}
